import { inject, Injectable } from '@angular/core';
import { CountryLanguages } from '@com-delta-idp/mnx-sitewide-shared-core-ui';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CountryLanguageDetails } from '../../models/utils';
import { CountryLanguageHelper } from '../../utils/country-language/country-language-helper';
import { CountryLanguageService } from '../../utils/country-language/country-language.service';
import { GeoLocationService } from '../geo-location/geo-location.service';
import { AppConfigService, CookieHelperService } from '../utils';
import { LocationService } from '../utils/location/location.service';
import { LanguageHandlerService } from './language-handler.service';
import { LanguageRedirectorService } from './language-redirector.service';

@Injectable({ providedIn: 'root' })
export class LanguageRedirectionHandlerService {
  cookieHelper = inject(CookieHelperService);
  appConfig = inject(AppConfigService);
  closestAirportCode = inject(GeoLocationService);
  countryLangSvc = inject(CountryLanguageService);
  countryLanguageHelper = inject(CountryLanguageHelper);
  languageHandler = inject(LanguageHandlerService);
  languageRedirector = inject(LanguageRedirectorService);
  locationService = inject(LocationService);

  unsubscribe = new Subject<void>();
  openCls = new Subject<boolean>();

  get openFlyoutLang() {
    return this.languageHandler.openFlyoutLang;
  }

  detectCurrentLanguage(): void {
    const countryCode = this.cookieHelper.getCountryCode();
    countryCode ? this.handleExistingCountry() : this.validateLocationCountry();
  }

  private validateLocationCountry(): void {
    const country = this.locationService.getUserCountry();
    this.countryLangSvc.getCountryLanguageData().subscribe(
      (data) => {
        const isValidCountry = data?.countryLanguages?.some(
          (item) => item.isoCode?.toLowerCase() === country?.toLowerCase()
        );
        if (isValidCountry) {
          this.processCountryData(country);
        } else {
          this.handleNewCountry();
        }
      },
      () => this.handleNewCountry()
    );
  }

  private handleNewCountry(): void {
    this.closestAirportCode
      .getClosestAirportCode$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        (data) =>
          data?.countrycode
            ? this.processCountryData(data.countrycode)
            : this.setDefaults(),
        () => this.setDefaults()
      );
  }

  private handleExistingCountry(): void {
    const prefCookie = this.cookieHelper.getCookieValue('prefUI') || '';
    const countryCode =
      this.countryLanguageHelper.getCountryCodeFromPrefCookie();
    this.countryLanguageHelper.setPreferredLanguages(prefCookie);

    this.countryLangSvc
      .getCountryLanguageData()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((data) => {
        if (!data) return;
        this.countryLanguageHelper.setData(data);
        this.redirectIfDomainIncorrect(data.countryLanguages);
        this.languageHandler.showModalIfNeeded(countryCode);
      });
  }

  private processCountryData(country: string): void {
    this.countryLangSvc
      .getCountryLanguageData()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((countryData: CountryLanguageDetails) => {
        this.countryLanguageHelper.setData(countryData);
        const countryCode = country.toLowerCase();
        const browserLang =
          this.countryLanguageHelper.detectBrowserLanguage() || '';
        const isSupported =
          this.countryLanguageHelper.isLanguageSupportedByCountry(
            countryCode,
            browserLang
          );

        isSupported
          ? this.languageHandler.handleSupportedLanguage(
              countryCode,
              browserLang,
              countryData
            )
          : this.languageHandler.handleUnsupportedLanguage(
              countryCode,
              countryData
            );
      });
  }

  private setDefaults(): void {
    const defaultPref =
      this.appConfig.getConfig('HEADER')?.['APP_PREF_COOKIE'] || '';
    this.countryLanguageHelper.setPrefCookie(defaultPref);
    this.countryLanguageHelper.setPreferredLanguages(defaultPref);
    this.openCls.next(true);
  }

  private redirectIfDomainIncorrect(
    countryLanguages: CountryLanguages[]
  ): void {
    if (!this.countryLanguageHelper.isDomainCorrectForPrefCookie()) {
      this.languageRedirector.loadSelectedLanguage(
        this.countryLanguageHelper.getCountryCodeFromPrefCookie() || '',
        this.countryLanguageHelper.getLanguageCodeFromPrefCookie() || '',
        countryLanguages
      );
    }
  }
}
